import {create} from 'zustand'

/**
 * @typedef {'info'|'warn'|'alert'|'update'} ToastVariant
 */

/**
 * @typedef {Object} Toast
 * @property {string} id
 * @property {string} title
 * @property {string} [body]
 * @property {ToastVariant} variant
 * @property {number} [ttlMs]                ms until auto-dismiss; 0 or undefined = persistent
 * @property {{label: string, onClick: () => void}} [action]
 */

const DEFAULT_TTL = {
    info: 4000,
    warn: 5000,
    alert: 8000,
    update: 0
}

let nextId = 1

/**
 * @typedef {Object} ToastStore
 * @property {Toast[]} toasts
 * @property {(toast: Omit<Toast,'id'>) => string} pushToast
 * @property {(id: string) => void} dismissToast
 * @property {() => void} clearToasts
 */

export const useToastStore = create((set, get) => ({
    /** @type {Toast[]} */
    toasts: [],

    /**
     * Adds a toast. Returns its id so callers can dismiss imperatively.
     * @param {Omit<Toast,'id'>} toast
     * @return {string}
     */
    pushToast: (toast) => {
        const id = `t${nextId++}`
        const ttlMs = toast.ttlMs ?? DEFAULT_TTL[toast.variant] ?? DEFAULT_TTL.info
        set(state => ({toasts: [...state.toasts, {...toast, id, ttlMs}]}))
        if (ttlMs > 0 && typeof window !== 'undefined') {
            window.setTimeout(() => {
                if (get().toasts.some(t => t.id === id))
                    get().dismissToast(id)
            }, ttlMs)
        }
        return id
    },

    /** @param {string} id */
    dismissToast: (id) => set(state => ({toasts: state.toasts.filter(t => t.id !== id)})),

    clearToasts: () => set({toasts: []})
}))
